import React, { useState, useEffect } from 'react';
import Loading from '../Common/Loading';
import CustomConfirmation from '../Common/CustomConfirmation';

interface MDStatusRow {
    student: string;
    startedMD: string;
    completedMD: string;
    lpCreated: string;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const styles = {
    container: {
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px',
        backgroundColor: '#FFFFFF', overflow: 'hidden'
    },
    pageTitle: {
        fontFamily: '"Times New Roman", Times, serif',
        background: 'white',
        fontSize: '12pt',
        fontWeight: 'bold' as 'bold',
        color: 'black',
        padding: '5px 10px 0px 10px',
        textAlign: 'center' as 'center'
    },
    tableScroll: {
        flex: 1,
        overflowY: 'auto' as const,
        overflowX: 'auto' as const,
        border: '1px solid #80A0FF',
        backgroundColor: '#FFFFFF',
    },
    tableContainer: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden'
    },
    dataTable: {
        width: '100%',
        borderCollapse: 'collapse' as const,
        fontSize: '11px',
        border: '1px solid #80A0FF',
    },

    th: {
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px',
        fontWeight: 'normal',
        border: '1px solid #80A0FF',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0,
        zIndex: 1,
        textWrap: "nowrap"
    },
    td: {
        padding: '8px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #80A0FF',
        color: 'black',
        textWrap: "nowrap"
    },

    footerSection: {
        padding: '10px',
        backgroundColor: '#ffffff',
        borderTop: '1px solid #80A0FF',
        textAlign: 'center' as 'center',
        display: 'flex',
        justifyContent: 'center',
        gap: '10px'
    },
    actionBtn: {
        fontFamily: 'Arial',
        cursor: 'pointer',
        border: '1px solid grey',
        backgroundColor: '#ebebeb',
        borderRadius: '2px',
        padding: '2px',
        fontSize: '9pt',
        color: 'black',
        width: '200px',
    }
};

const EL_MDStatus: React.FC = () => {
    const [mdStatusData, setMdStatusData] = useState<MDStatusRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<string>('');
    const [showConfirm, setShowConfirm] = useState(false);

    const searchParams = new URLSearchParams(window.location.search);
    const schoolCode = searchParams.get('C') || '';
    const familyCode = searchParams.get('F') || '';
    const tutorLogin = searchParams.get('T') || '';

    useEffect(() => {
        if (schoolCode && familyCode) {
            fetchMDStatus();
        }
    }, [schoolCode, familyCode]);

    const fetchMDStatus = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}api/el_md_status/?school_code=${schoolCode}&family_code=${familyCode}`);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setMdStatusData(data.results || []);
        } catch (error) {
            console.error('Error fetching MD Status:', error);
        } finally {
            setLoading(false);
        }
    };

    const confirmSendMail = () => {
        setShowConfirm(true);
    };

    const executeSendMail = async () => {
        setShowConfirm(false);
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}api/el_md_status/activate_wlp/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    school_code: schoolCode,
                    family_code: familyCode,
                    tutor_login: tutorLogin
                }),
            });
            const result = await response.json();
            if (result.success) {
                setMessage(result.message);
                fetchMDStatus();
            } else {
                alert('Error: ' + result.message);
            }
        } catch (error) {
            console.error('Error sending mail:', error);
            alert('Failed to process request.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            {loading && <Loading />}

            <div style={styles.tableContainer}>
                <div style={styles.pageTitle}>
                    Status of Student's Maths Doctor
                </div>
                <div style={styles.tableScroll}>
                <table style={styles.dataTable}>
                    <thead>
                        <tr>
                            <th style={styles.th}>#</th>
                            <th style={styles.th}>Student</th>
                            <th style={{ ...styles.th, textAlign: 'center' }}>StartedMD</th>
                            <th style={{ ...styles.th, textAlign: 'center' }}>CompletedMD</th>
                            <th style={{ ...styles.th, borderRight: 'none', width: '1532px' }}>LPCreated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mdStatusData.length > 0 ? (
                            mdStatusData.map((row, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#F4FAFF' : 'white' }}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>{row.student}</td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>{row.startedMD}</td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>{row.completedMD}</td>
                                    <td style={{ ...styles.td, borderRight: 'none' }}>{row.lpCreated}</td>
                                </tr>
                            ))
                        ) : (
                            !loading && (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                        No records found.
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            <div style={styles.footerSection}>
                <button
                    onClick={confirmSendMail}
                    style={styles.actionBtn}
                >
                    Send Mail & Activate WLP
                </button>
                <button
                    onClick={() => window.close()}
                    style={styles.actionBtn}
                >
                    Close
                </button>
            </div>

            {showConfirm && (
                <CustomConfirmation
                    message="Do you want to send mail & activate WLP?"
                    onConfirm={executeSendMail}
                    onClose={() => setShowConfirm(false)}
                />
            )}
        </div>
    );
};

export default EL_MDStatus;